import { create } from 'zustand';

import type { MegaWorldId } from '../generated/MegaWorldId';
import type { ServerVisibility } from '../generated/ServerVisibility';
import { debugLog } from '../log';
import { useToasts } from '../toasts';
import {
  expandClaim,
  foundWorld,
  getClaims,
  getClaimChunks,
  getClaimTiles,
  getLakes,
  getMegaWorlds,
  getMyClaims,
  getRivers,
  shrinkClaim,
  type Bounds,
  type ChunkCoordJson,
  type ClaimMetaJson,
  type ClaimOverlayJson,
  type ClaimTilesOverlayJson,
  type ExpandClaimResponseJson,
  type FoundWorldResponse,
  type LakeOverlayJson,
  type MegaWorldInfoJson,
  type RiverSegmentJson,
  type ShrinkClaimResponseJson,
  type ValidationResultJson,
} from './api';

export type PaintTool = 'brush' | 'fill' | 'snap' | 'eraser';

export interface UndoAction {
  type: 'add' | 'remove';
  chunks: ChunkCoordJson[];
}

export interface ExpansionState {
  expandingClaimId: number | null;
  expandingClaim: ClaimMetaJson | null;
  expandingChunks: Set<string>;
  pendingAdditions: Set<string>;
  pendingRemovals: Set<string>;
  activeTool: PaintTool;
  brushSize: number;
  undoStack: UndoAction[];
  validationErrors: ChunkCoordJson[];
  islandChunks: ChunkCoordJson[];
  commitInProgress: boolean;
}

export const BLOCK_SIZE = 128;

export interface EmbarkState extends ExpansionState {
  megaWorlds: MegaWorldInfoJson[];
  selectedMegaWorld: MegaWorldId | null;
  myClaims: ClaimMetaJson[];
  rivers: RiverSegmentJson[];
  lakes: LakeOverlayJson[];
  visibleClaims: ClaimOverlayJson[];
  claimTiles: ClaimTilesOverlayJson[];
  placementMode: boolean;
  selectedChunk: ChunkCoordJson | null;

  fetchMegaWorlds: () => Promise<void>;
  selectMegaWorld: (id: MegaWorldId) => void;
  fetchMyClaims: () => Promise<void>;
  fetchRiversAndLakes: (bounds: Bounds) => Promise<void>;
  fetchClaims: (bounds: Bounds) => Promise<void>;
  fetchClaimTiles: (bounds: Bounds) => Promise<void>;
  enterPlacement: () => void;
  exitPlacement: () => void;
  selectChunk: (chunk: ChunkCoordJson | null) => void;
  doFoundWorld: (
    name: string,
    visibility: ServerVisibility,
  ) => Promise<FoundWorldResponse | null>;

  enterExpansion: (claimId: number) => Promise<void>;
  exitExpansion: () => void;
  setActiveTool: (tool: PaintTool) => void;
  setBrushSize: (size: number) => void;
  paintChunks: (chunks: ChunkCoordJson[]) => void;
  eraseChunks: (chunks: ChunkCoordJson[]) => void;
  undo: () => void;
  commitExpansion: () => Promise<boolean>;
}

export function chunkKey(c: ChunkCoordJson): string {
  return `${c.x},${c.y}`;
}

function parseKey(k: string): ChunkCoordJson {
  const [x, y] = k.split(',').map(Number);
  return { x: x!, y: y! };
}

const EXPANSION_INITIAL: ExpansionState = {
  expandingClaimId: null,
  expandingClaim: null,
  expandingChunks: new Set(),
  pendingAdditions: new Set(),
  pendingRemovals: new Set(),
  activeTool: 'brush',
  brushSize: 1,
  undoStack: [],
  validationErrors: [],
  islandChunks: [],
  commitInProgress: false,
};

export const useEmbark = create<EmbarkState>((set, get) => ({
  megaWorlds: [],
  selectedMegaWorld: null,
  myClaims: [],
  rivers: [],
  lakes: [],
  visibleClaims: [],
  claimTiles: [],
  placementMode: false,
  selectedChunk: null,
  ...EXPANSION_INITIAL,

  fetchMegaWorlds: async () => {
    try {
      const worlds = await getMegaWorlds();
      set({ megaWorlds: worlds });
    } catch (e) {
      useToasts
        .getState()
        .push('error', `Failed to load mega-worlds: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  selectMegaWorld: (id) =>
    set({
      selectedMegaWorld: id,
      rivers: [],
      lakes: [],
      visibleClaims: [],
      claimTiles: [],
      placementMode: false,
      selectedChunk: null,
    }),

  fetchMyClaims: async () => {
    try {
      const claims = await getMyClaims();
      set({ myClaims: claims });
    } catch (e) {
      useToasts
        .getState()
        .push('error', `Failed to load claims: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  fetchRiversAndLakes: async (bounds) => {
    const mw = get().selectedMegaWorld;
    if (!mw) return;
    try {
      const [rivers, lakes] = await Promise.all([
        getRivers(mw, bounds),
        getLakes(mw, bounds),
      ]);
      set({ rivers, lakes });
    } catch {
      // Silently skip — river/lake data is optional enrichment
    }
  },

  fetchClaims: async (bounds) => {
    const mw = get().selectedMegaWorld;
    if (!mw) return;
    try {
      const claims = await getClaims(mw, bounds);
      set({ visibleClaims: claims });
    } catch (e) {
      useToasts
        .getState()
        .push('error', `Failed to load claims: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  fetchClaimTiles: async (bounds) => {
    const mw = get().selectedMegaWorld;
    if (!mw) return;
    try {
      const tiles = await getClaimTiles(mw, bounds);
      const totalTiles = tiles.reduce((s, c) => s + c.tiles.length, 0);
      debugLog('world', 'debug', `fetchClaimTiles bounds=(${bounds.x1},${bounds.y1})-(${bounds.x2},${bounds.y2}) → ${tiles.length} claims, ${totalTiles} tiles`);
      set({ claimTiles: tiles });
    } catch {
      // Non-critical — claim tiles are a rendering enhancement
    }
  },

  enterPlacement: () => set({ placementMode: true }),

  exitPlacement: () => set({ placementMode: false, selectedChunk: null }),

  selectChunk: (chunk) => set({ selectedChunk: chunk }),

  doFoundWorld: async (name, visibility) => {
    const { selectedMegaWorld, selectedChunk } = get();
    if (!selectedMegaWorld || !selectedChunk) return null;
    const origin = {
      x: Math.floor(selectedChunk.x / BLOCK_SIZE) * BLOCK_SIZE,
      y: Math.floor(selectedChunk.y / BLOCK_SIZE) * BLOCK_SIZE,
    };
    try {
      const resp = await foundWorld({
        name,
        visibility,
        mega_world: selectedMegaWorld,
        origin,
      });
      await get().fetchMyClaims();
      set({ placementMode: false, selectedChunk: null });
      return resp;
    } catch (e) {
      useToasts
        .getState()
        .push('error', `Failed: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  },

  enterExpansion: async (claimId) => {
    const claim = get().myClaims.find((c) => c.claim_id === claimId);
    if (!claim) return;
    try {
      const chunks = await getClaimChunks(claimId);
      const chunkSet = new Set(chunks.map(chunkKey));
      set({
        ...EXPANSION_INITIAL,
        expandingClaimId: claimId,
        expandingClaim: claim,
        expandingChunks: chunkSet,
        placementMode: false,
        selectedChunk: null,
      });
    } catch (e) {
      useToasts
        .getState()
        .push('error', `Failed to load claim chunks: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  exitExpansion: () => set({ ...EXPANSION_INITIAL }),

  setActiveTool: (tool) => set({ activeTool: tool }),

  setBrushSize: (size) => set({ brushSize: Math.max(1, Math.min(128, size)) }),

  paintChunks: (chunks) => {
    if (chunks.length === 0) return;
    set((s) => {
      const nextAdd = new Set(s.pendingAdditions);
      const nextRem = new Set(s.pendingRemovals);
      const added: ChunkCoordJson[] = [];
      for (const c of chunks) {
        const k = chunkKey(c);
        if (s.expandingChunks.has(k) && !nextRem.has(k)) continue;
        if (nextAdd.has(k)) continue;
        if (nextRem.has(k)) {
          nextRem.delete(k);
          added.push(c);
        } else {
          nextAdd.add(k);
          added.push(c);
        }
      }
      if (added.length === 0) return s;
      return {
        pendingAdditions: nextAdd,
        pendingRemovals: nextRem,
        undoStack: [...s.undoStack, { type: 'add' as const, chunks: added }],
        validationErrors: [],
        islandChunks: [],
      };
    });
  },

  eraseChunks: (chunks) => {
    if (chunks.length === 0) return;
    set((s) => {
      const nextAdd = new Set(s.pendingAdditions);
      const nextRem = new Set(s.pendingRemovals);
      const removed: ChunkCoordJson[] = [];
      for (const c of chunks) {
        const k = chunkKey(c);
        if (s.expandingClaim && c.x === s.expandingClaim.origin.x && c.y === s.expandingClaim.origin.y) continue;
        if (nextAdd.has(k)) {
          nextAdd.delete(k);
          removed.push(c);
        } else if (s.expandingChunks.has(k) && !nextRem.has(k)) {
          nextRem.add(k);
          removed.push(c);
        }
      }
      if (removed.length === 0) return s;
      return {
        pendingAdditions: nextAdd,
        pendingRemovals: nextRem,
        undoStack: [...s.undoStack, { type: 'remove' as const, chunks: removed }],
        validationErrors: [],
        islandChunks: [],
      };
    });
  },

  undo: () => {
    set((s) => {
      if (s.undoStack.length === 0) return s;
      const stack = [...s.undoStack];
      const action = stack.pop()!;
      const nextAdd = new Set(s.pendingAdditions);
      const nextRem = new Set(s.pendingRemovals);
      for (const c of action.chunks) {
        const k = chunkKey(c);
        if (action.type === 'add') {
          nextAdd.delete(k);
          if (s.expandingChunks.has(k)) nextRem.add(k);
        } else {
          if (s.expandingChunks.has(k)) {
            nextRem.delete(k);
          } else {
            nextAdd.add(k);
          }
        }
      }
      return {
        undoStack: stack,
        pendingAdditions: nextAdd,
        pendingRemovals: nextRem,
        validationErrors: [],
        islandChunks: [],
      };
    });
  },

  commitExpansion: async () => {
    const s = get();
    if (!s.expandingClaimId) return false;
    set({ commitInProgress: true, validationErrors: [], islandChunks: [] });

    let ok = true;
    const additions = [...s.pendingAdditions].map(parseKey);
    const removals = [...s.pendingRemovals].map(parseKey);

    if (additions.length > 0) {
      try {
        const resp: ExpandClaimResponseJson = await expandClaim(s.expandingClaimId, additions, true);
        const newChunks = new Set(s.expandingChunks);
        for (const c of resp.added_chunks) newChunks.add(chunkKey(c));
        set((prev) => ({
          expandingChunks: newChunks,
          expandingClaim: resp.claim as unknown as ClaimMetaJson,
          pendingAdditions: new Set<string>(),
          myClaims: prev.myClaims.map((cl) =>
            cl.claim_id === s.expandingClaimId ? (resp.claim as unknown as ClaimMetaJson) : cl,
          ),
        }));
      } catch (e) {
        ok = false;
        const errText = e instanceof Error ? e.message : String(e);
        try {
          const parsed = JSON.parse(errText.replace(/^\d+:\s*/, ''));
          if (parsed.validation) {
            const v = parsed.validation as ValidationResultJson;
            const errChunks = [
              ...v.contiguity.disconnected_chunks,
              ...v.min_width.narrow_chunks,
            ];
            set({
              validationErrors: errChunks,
              islandChunks: v.islands.enclosed_chunks,
              commitInProgress: false,
            });
            if (!v.contiguity.passed) {
              useToasts.getState().push('error', `${v.contiguity.disconnected_chunks.length} disconnected chunk(s)`);
            } else if (!v.min_width.passed) {
              useToasts.getState().push('error', `${v.min_width.narrow_chunks.length} narrow chunk(s)`);
            }
            return false;
          }
        } catch {
          // not JSON
        }
        useToasts.getState().push('error', `Expand failed: ${errText}`);
        set({ commitInProgress: false });
        return false;
      }
    }

    if (removals.length > 0) {
      try {
        const resp: ShrinkClaimResponseJson = await shrinkClaim(s.expandingClaimId, removals);
        const newChunks = new Set(get().expandingChunks);
        for (const c of resp.removed_chunks) newChunks.delete(chunkKey(c));
        set((prev) => ({
          expandingChunks: newChunks,
          expandingClaim: resp.claim as unknown as ClaimMetaJson,
          pendingRemovals: new Set<string>(),
          myClaims: prev.myClaims.map((cl) =>
            cl.claim_id === s.expandingClaimId ? (resp.claim as unknown as ClaimMetaJson) : cl,
          ),
        }));
      } catch (e) {
        ok = false;
        useToasts.getState().push('error', `Shrink failed: ${e instanceof Error ? e.message : String(e)}`);
        set({ commitInProgress: false });
        return false;
      }
    }

    if (ok) {
      set({ ...EXPANSION_INITIAL, commitInProgress: false });
      useToasts.getState().push('success', 'Claim updated!');
    }
    return ok;
  },

}));
